<script setup lang="ts">
import { getCategoryUrl } from '~/utils/route-utils'
import type { AppDetailsSchema } from '~/types/product.model'
import type { LinkTrackingData } from '~/types/tracking'

const props = defineProps<{
  data: AppDetailsSchema
}>()

const { origin } = useRequestURL()

// TODO: a bit unpredictable and not nice to do texts on FE (SC-65110)
const minPlanText = computed(() => {
  const title = props.data.min_sendcloud_plan?.title
  const lowerCaseTitle = title?.toLowerCase()
  if (!title) {
    return null
  }
  if (lowerCaseTitle === 'free') {
    return 'Available for all Sendcloud plans'
  }
  let text = `Available for Sendcloud ${title} plans`
  // TODO: not nice to have these hardcoded (SC-65110)
  if (lowerCaseTitle !== 'enterprise') {
    text += ' and up'
  }
  return text
})

const minAppPlanText = computed(() => {
  const { data } = props
  const { min_app_plan } = data.requirements
  if (!min_app_plan) {
    return null
  }
  if (min_app_plan.toLowerCase() === 'free') {
    return `${data.title} account required`
  }
  return `${data.requirements.min_app_plan} ${data.title} account required`
})

const numberOfInstallsText = computed(() => {
  return getNumberOfInstallsBracket(props.data.number_of_installs ?? 0)
})

function getNumberOfInstallsBracket(numberOfInstalls: number): string {
  if (numberOfInstalls <= 10) {
    return '<10'
  }
  if (numberOfInstalls <= 100) {
    return '10+'
  }
  if (numberOfInstalls <= 1000) {
    return '100+'
  }
  if (numberOfInstalls <= 5000) {
    return '1k+'
  }
  if (numberOfInstalls <= 10000) {
    return '5k+'
  }
  if (numberOfInstalls <= 20000) {
    return '10k+'
  }
  return '20k+'
}

const trackLinkClick = useTrackLinkClick()
const trackSidebarLinkClick = ({
  linkType,
  linkUrl,
  destinationId,
  destinationTitle,
}: Required<LinkTrackingData>): void => {
  trackLinkClick({
    linkType,
    linkUrl,
    destinationId,
    destinationTitle,
    pageLocation: 'Sidebar',
  })
}
</script>

<template>
  <div class="app-features">
    <section>
      <h2 class="heading-5">
        Details
      </h2>
      <dl class="app-features__list">
        <ProductReviewRatingMinimal
          :rating-stats="data.rating_stats"
          :show-when-no-ratings="true"
        />
        <div
          v-if="minPlanText || minAppPlanText"
          class="app-features__item"
        >
          <dd v-if="minPlanText">
            {{ minPlanText }}
          </dd>

          <dd v-if="minAppPlanText">
            {{ minAppPlanText }}
          </dd>
        </div>
        <div
          v-if="data.number_of_installs"
          class="app-features__item"
        >
          <dt>Number of installs</dt>
          <dd data-test="number-of-installs">
            {{ numberOfInstallsText }}
          </dd>
        </div>
        <div
          v-if="data.developer"
          class="app-features__item"
        >
          <dt>Developer</dt>
          <dd data-test="developer">
            <NuxtLink
              v-if="data.developer.url"
              target="_blank"
              :to="data.developer.url"
              @click="
                trackSidebarLinkClick({
                  destinationId: data.developer.id,
                  destinationTitle: data.developer.name,
                  linkType: 'Developer website',
                  linkUrl: data.developer.url,
                })
              "
            >
              {{ data.developer.name }}
            </NuxtLink>
            <template v-else>
              {{ data.developer.name }}
            </template>
          </dd>
        </div>
        <div
          v-if="data.category"
          class="app-features__item"
        >
          <dt>Category</dt>
          <dd data-test="category">
            <NuxtLink
              :to="getCategoryUrl(data.category.slug)"
              @click="
                trackSidebarLinkClick({
                  destinationId: data.category.slug,
                  destinationTitle: data.category.title,
                  linkType: 'Category',
                  linkUrl: getCategoryUrl(data.category.slug, origin),
                })
              "
            >
              {{ data.category.title }}
            </NuxtLink>
          </dd>
        </div>
        <div
          v-if="data.countries.length"
          class="app-features__item"
        >
          <dt>Countries available in</dt>
          <dd data-test="countries">
            {{ data.countries.map(country => country.name).join(', ') }}
          </dd>
        </div>
        <div
          v-if="data.languages.length"
          class="app-features__item"
        >
          <dt>Available languages</dt>
          <dd data-test="languages">
            {{ data.languages.map(language => language.name).join(', ') }}
          </dd>
        </div>
      </dl>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.app-features {
  @include max-width('lg') {
    @include card-mixin;

    padding: var(--app-spacer-32);
  }

  @include min-width('lg') {
    padding: 0 var(--app-spacer-32);
  }
}

.app-features__list {
  display: flex;
  flex-direction: column;
  margin: 0;

  .app-features__item {
    padding: var(--app-spacer-10) 0;

    dt {
      @extend %font-16-24-bold;

      margin-right: var(--app-spacer-4);
    }

    dd {
      margin-left: 0;
    }

    dt,
    dd {
      display: inline-block;

      &::first-letter {
        text-transform: capitalize;
      }
    }

    dd ~ dd {
      margin-top: var(--app-spacer-6);
      display: block;
    }
  }
}
</style>
